import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';

// Performance optimization: Create a shared AbortController for request cancellation
let sharedAbortController = null;

// Performance optimization: Connection pooling simulation with request caching
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Performance optimization: Optimized audio constraints for better performance
const OPTIMIZED_AUDIO_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: false, // Disabled for better performance as per server optimization
    sampleRate: 16000,
    channelCount: 1,
    latency: 0.01, // 10ms low-latency audio capture
  }
};

/**
 * SimpleTranscribe Component - Performance Optimized
 *
 * A minimal transcription component with just a mic icon for start/stop recording.
 * Follows KISS principles and provides a clean API for transcription functionality.
 *
 * Performance optimizations applied:
 * - HTTP connection pooling simulation with caching
 * - Optimized audio constraints for low-latency capture
 * - Reduced logging for better performance
 * - Enhanced error handling with cleanup
 * - Request cancellation support
 *
 * Props:
 * - onTranscriptionUpdate: (text) => void - Called when new transcription text is available
 * - onError: (error) => void - Called when an error occurs
 * - onRecordingStateChange: (isRecording) => void - Called when recording state changes
 * - className: string - Additional CSS classes
 * - size: 'sm' | 'md' | 'lg' - Icon size
 * - disabled: boolean - Disable the component
 * - sonioxUrl: string - Optional Soniox API URL override
 * - translateToEnglish: boolean - Enable translation to English (default: false)
 * - enableEndTags: boolean - Enable endpoint detection (default: true)
 * - context: string - Direct context string for better transcription accuracy
 * - contextUrl: string - URL to fetch context from (alternative to direct context)
 * - onContextLoaded: (context) => void - Called when context is successfully loaded
 * - onContextError: (error) => void - Called when context loading fails
 * - languageHints: string[] - Language hints for mixed-language scenarios (default: ["en"])
 * - enableLanguageIdentification: boolean - Enable per-token language identification (default: false)
 * - enableSpeakerDiarization: boolean - Enable speaker diarization (default: false)
 * - maxNonFinalTokensDurationMs: number - Balance accuracy vs responsiveness (default: 6000)
 */
const SimpleTranscribe = ({
  onTranscriptionUpdate = () => {},
  onError = () => {},
  onRecordingStateChange = () => {},
  className = '',
  size = 'md',
  disabled = false,
  sonioxUrl = null,
  translateToEnglish = false,
  enableEndTags = true,
  context = null,
  contextUrl = null,
  onContextLoaded = () => {},
  onContextError = () => {},
  languageHints = ["en"],
  enableLanguageIdentification = false,
  enableSpeakerDiarization = false,
  maxNonFinalTokensDurationMs = 6000
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedContext, setLoadedContext] = useState(null);
  const recordTranscribeRef = useRef(null);

  // Size configurations
  const sizeConfig = {
    sm: 'w-8 h-8 p-1.5',
    md: 'w-12 h-12 p-3',
    lg: 'w-16 h-16 p-4'
  };

  const iconSizeConfig = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  // Context loading functionality
  const loadContextFromUrl = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch context: ${response.status} ${response.statusText}`);
      }
      const contextText = await response.text();

      // Validate context length (Soniox supports up to 20,000 characters)
      if (contextText.length > 20000) {
        console.warn('Context text exceeds 20,000 characters and will be truncated');
        return contextText.substring(0, 20000);
      }

      return contextText;
    } catch (error) {
      onContextError(error);
      return null;
    }
  }, [onContextError]);

  // Effect to load context from URL when contextUrl changes
  useEffect(() => {
    if (contextUrl) {
      loadContextFromUrl(contextUrl).then((contextText) => {
        if (contextText) {
          setLoadedContext(contextText);
          onContextLoaded(contextText);
        }
      });
    } else {
      setLoadedContext(null);
    }
  }, [contextUrl, loadContextFromUrl, onContextLoaded]);

  // Get the effective context (direct context takes precedence over loaded context)
  const getEffectiveContext = useCallback(() => {
    if (context) {
      // Validate direct context length
      if (context.length > 20000) {
        console.warn('Direct context exceeds 20,000 characters and will be truncated');
        return context.substring(0, 20000);
      }
      return context;
    }
    return loadedContext;
  }, [context, loadedContext]);

  // Performance optimization: Cached API key fetching with connection pooling
  const fetchApiKeyOptimized = useCallback(async () => {
    const cacheKey = 'soniox_temp_key_simple';
    const now = Date.now();

    // Check cache first for performance
    if (requestCache.has(cacheKey)) {
      const cached = requestCache.get(cacheKey);
      if (now - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }
      requestCache.delete(cacheKey);
    }

    // Cancel any previous request
    if (sharedAbortController) {
      sharedAbortController.abort();
    }
    sharedAbortController = new AbortController();

    const startTime = performance.now();
    // Use provided sonioxUrl or fall back to environment variable or localhost
    const backendUrl = sonioxUrl || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

    // Performance optimization: Enhanced fetch with timeout and abort signal
    const response = await fetch(`${backendUrl}/api/soniox/temp-key`, {
      method: 'POST',
      signal: sharedAbortController.signal,
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      mode: 'cors',
      credentials: 'omit',
    });

    const duration = performance.now() - startTime;

    // Performance monitoring: Reduced logging as per server optimization
    if (process.env.NODE_ENV === 'development') {
      console.log(`SimpleTranscribe API Key Request: ${response.status} (${duration.toFixed(1)}ms)`);
    }

    if (!response.ok) {
      throw new Error(`Failed to get API key: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`API Key Error: ${data.error}`);
    }

    const apiKey = data.api_key;
    if (!apiKey) {
      throw new Error('No API key received from server');
    }

    // Cache the result for performance
    requestCache.set(cacheKey, {
      data: apiKey,
      timestamp: now
    });

    return apiKey;
  }, [sonioxUrl]);

  const startRecording = useCallback(async () => {
    try {
      setIsLoading(true);

      // Performance optimization: Use optimized API key fetching
      const apiKey = await fetchApiKeyOptimized();

      // Dynamic import of Soniox library
      const { RecordTranscribe } = await import('@soniox/speech-to-text-web');
      recordTranscribeRef.current = new RecordTranscribe({
        apiKey,
        // Performance optimization: Add audio constraints for better performance
        audioConstraints: OPTIMIZED_AUDIO_CONSTRAINTS.audio
      });

      // Performance optimization: Start transcription with optimized settings
      const effectiveContext = getEffectiveContext();
      const startConfig = {
        model: "stt-rt-preview-v2",
        language_hints: translateToEnglish ? ["en"] : languageHints,
        enable_endpoint_detection: enableEndTags,
        enable_language_identification: translateToEnglish || enableLanguageIdentification,
        enable_speaker_diarization: enableSpeakerDiarization,
        max_non_final_tokens_duration_ms: maxNonFinalTokensDurationMs,
        // Performance optimization: Optimized chunk settings (increased from 100ms to 250ms)
        chunk_length_ms: 250, // Reduced network overhead as per server optimization
      };

      // Add context if available
      if (effectiveContext) {
        startConfig.context = effectiveContext;
      }

      // Add translation configuration if translateToEnglish is enabled
      if (translateToEnglish) {
        startConfig.translation = {
          type: "one_way",
          target_language: "en"
        };
      }

      await recordTranscribeRef.current.start({
        ...startConfig,
        onStarted: () => {
          // Performance optimization: Reduced logging
          if (process.env.NODE_ENV === 'development') {
            console.log('SimpleTranscribe: Recording started');
          }
          setIsRecording(true);
          onRecordingStateChange(true);
        },
        onPartialResult: (result) => {
          if (result.tokens && result.tokens.length > 0) {
            let transcribedText = '';

            // Performance optimization: Use for...of loop for better performance
            for (const token of result.tokens) {
              if (token.is_final) {
                transcribedText += token.text;
              }
            }

            if (transcribedText) {
              onTranscriptionUpdate(transcribedText);
            }
          }
        },
        onFinished: () => {
          setIsRecording(false);
          onRecordingStateChange(false);
          // Performance optimization: Clear abort controller
          if (sharedAbortController) {
            sharedAbortController = null;
          }
        },
        onError: (status, message) => {
          const error = new Error(`Transcription error: ${status} - ${message}`);
          onError(error);
          setIsRecording(false);
          onRecordingStateChange(false);
          // Performance optimization: Clear abort controller on error
          if (sharedAbortController) {
            sharedAbortController = null;
          }
        }
      });

    } catch (error) {
      // Performance optimization: Enhanced error handling with cleanup
      if (error.name === 'AbortError') {
        onError(new Error('Request was cancelled'));
      } else {
        onError(error);
      }
      setIsRecording(false);
      onRecordingStateChange(false);

      // Performance optimization: Cleanup on error
      if (sharedAbortController) {
        sharedAbortController = null;
      }
    } finally {
      setIsLoading(false);
    }
  }, [onTranscriptionUpdate, onError, onRecordingStateChange, fetchApiKeyOptimized, translateToEnglish, enableEndTags, getEffectiveContext, languageHints, enableLanguageIdentification, enableSpeakerDiarization, maxNonFinalTokensDurationMs]);

  const stopRecording = useCallback(async () => {
    if (recordTranscribeRef.current && isRecording) {
      try {
        await recordTranscribeRef.current.stop();
      } catch (error) {
        // Performance optimization: Silent error handling for stop operation
        if (process.env.NODE_ENV === 'development') {
          console.warn('SimpleTranscribe: Error stopping recording:', error);
        }
      } finally {
        // Performance optimization: Cleanup abort controller
        if (sharedAbortController) {
          sharedAbortController = null;
        }
        // Performance optimization: Clear cache when stopping
        requestCache.clear();
      }
    }
  }, [isRecording]);

  const handleClick = useCallback(() => {
    if (disabled || isLoading) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, disabled, isLoading, startRecording, stopRecording]);

  const buttonClasses = `
    ${sizeConfig[size]}
    rounded-full
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    ${disabled || isLoading 
      ? 'bg-gray-400 cursor-not-allowed' 
      : isRecording 
        ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500 shadow-lg shadow-red-500/25' 
        : 'bg-green-500 hover:bg-green-600 focus:ring-green-500 shadow-lg shadow-green-500/25'
    }
    ${className}
  `.trim();

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={buttonClasses}
      title={isRecording ? 'Stop Recording' : 'Start Recording'}
      aria-label={isRecording ? 'Stop Recording' : 'Start Recording'}
    >
      {isLoading ? (
        <div className={`${iconSizeConfig[size]} animate-spin rounded-full border-2 border-white border-t-transparent`} />
      ) : isRecording ? (
        <MicOff className={`${iconSizeConfig[size]} text-white`} />
      ) : (
        <Mic className={`${iconSizeConfig[size]} text-white`} />
      )}
    </button>
  );
};

export default SimpleTranscribe;
